/**
 * Booking date RANGE — start + end on a civil timeline, any length.
 *
 * Two months side-by-side (or one on the phone) is a VIEWPORT. Chevrons
 * move that window. They never write start/end. A tap in November after
 * paging from September is the same function as a tap on October 2.
 *
 * The money-path calendar used to pass `current={startDate}` into
 * react-native-calendars, so 30 September pinned the grid and October
 * could not stay on screen. Extra days of other months were also treated
 * as `state === 'disabled'`. This file is the value; the grid is paint.
 */

export type DateRangeField = 'pickup' | 'return'

export function dateRangeCalendarUsesTwoMonths(isDesktop: boolean, isTablet: boolean): boolean {
  return isDesktop || isTablet
}

export function monthStartFromDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  return `${iso.slice(0, 7)}-01`
}

export function addMonthsToMonthStart(monthStart: string, delta: number): string {
  const year = Number(monthStart.slice(0, 4))
  const month = Number(monthStart.slice(5, 7))
  if (!Number.isFinite(year) || !Number.isFinite(month)) return monthStart
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1))
  const y = shifted.getUTCFullYear()
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0')
  return `${y}-${m}-01`
}

export function addCalendarDaysISO(iso: string, days: number): string {
  const date = new Date(`${iso}T12:00:00`)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function clampDisplayedMonth(monthStart: string, minDate: string, maxDate: string): string {
  const minMonth = monthStartFromDate(minDate)
  const maxMonth = monthStartFromDate(maxDate)
  if (monthStart < minMonth) return minMonth
  if (monthStart > maxMonth) return maxMonth
  return monthStart
}

/**
 * Viewport convenience only. After a late-month pickup on a one-month
 * phone grid, show the next month so a short return is on screen. The
 * guest can still page to any later month; this never writes the range.
 */
export function monthToRevealAfterPickup(startDate: string, usesTwoMonths: boolean): string {
  const startMonth = monthStartFromDate(startDate)
  if (usesTwoMonths) return startMonth
  const day = Number(startDate.slice(8, 10))
  if (Number.isFinite(day) && day >= 21) return addMonthsToMonthStart(startMonth, 1)
  return startMonth
}

/**
 * One tap, any visible month.
 *
 * - No start, a day on/before start, or the pickup field is active →
 *   that day is the new start (end cleared). Recogida is how a guest
 *   moves start forward to any later month.
 * - Same day as start → start stays, end cleared.
 * - A later day while return is active → end, unless it violates host
 *   min duration or a hold.
 * Visible months are not an argument. 15 December after paging from
 * September is the same as 2 October next door.
 */
export function applyDateRangeSelection(params: {
  tapped: string
  startDate: string | null
  minReturnDate: string | null
  maxReturnDate: string | null
  activeField?: DateRangeField
}): { startDate: string; endDate: string | null; field: DateRangeField } | null {
  const { tapped, startDate, minReturnDate, maxReturnDate, activeField } = params
  if (!startDate || tapped <= startDate || activeField === 'pickup') {
    return { startDate: tapped, endDate: null, field: 'return' }
  }
  if (minReturnDate && tapped < minReturnDate) return null
  if (maxReturnDate && tapped > maxReturnDate) return null
  return { startDate, endDate: tapped, field: 'return' }
}

export type BookingCalendarBlock =
  | 'loading'
  | 'before_min'
  | 'held'
  | 'no_pickup_slot'
  | 'min_duration'
  | 'crosses_hold'

/**
 * Why a painted day cannot start or end a trip.
 *
 * The grid stays tappable (`disabled={false}`) so a grey cell can explain
 * itself. Silent return was a dead tap: the guest thought the calendar
 * was broken. This is the reason the toast speaks.
 */
export function bookingCalendarDayBlock(params: {
  tapped: string
  minDate: string
  availabilityReady: boolean
  isHeld: boolean
  pickupSlot: string | null
  startDate: string | null
  activeField: DateRangeField
  minReturnDate: string | null
  maxReturnDate: string | null
}): BookingCalendarBlock | null {
  const {
    tapped,
    minDate,
    availabilityReady,
    isHeld,
    pickupSlot,
    startDate,
    activeField,
    minReturnDate,
    maxReturnDate,
  } = params
  if (!availabilityReady) return 'loading'
  if (tapped < minDate) return 'before_min'
  if (isHeld) return 'held'
  const wouldResetStart = !startDate || tapped < startDate || activeField === 'pickup'
  if (wouldResetStart) {
    return pickupSlot === null ? 'no_pickup_slot' : null
  }
  if (startDate && minReturnDate && tapped > startDate && tapped < minReturnDate)
    return 'min_duration'
  if (maxReturnDate && tapped > maxReturnDate) return 'crosses_hold'
  return null
}

export function datesBetweenExclusive(start: string, end: string): string[] {
  const dates: string[] = []
  let current = addCalendarDaysISO(start, 1)
  while (current < end) {
    dates.push(current)
    current = addCalendarDaysISO(current, 1)
  }
  return dates
}
